import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CalendarDays, ChevronLeft, ChevronRight, FileText, Image as ImageIcon, MapPin, Package, Pencil, Plus, Save, Trash2, X } from 'lucide-react';
import toast from 'react-hot-toast';
import API from '../../utils/api';
import { useLocationLists, lookupPin } from '../../utils/locationApi';
import { useConfirmDialog } from '../../hooks/useConfirmDialog';
import DatePicker from '../../components/DatePicker';
import Select from '../../components/Select';
import { mediaUrl } from '../Profiles/profileShared';
import './ProviderServiceManager.css';

// Admin window for one service of a provider: edit the details and photos, manage its packages, and edit its
// availability calendar. Everything is saved straight to the server (routes/adminServiceTools.js).
const BASE = '/admin/service-tools';
const fileUrl = path => (!path ? '' : /^https?:/i.test(path) ? path : mediaUrl(path));
const errorText = (error, fallback) => error.response?.data?.message || fallback;
const keyOf = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const utcKey = (value) => new Date(value).toISOString().slice(0, 10);

// the business details (the address has its own block below, in the standard format)
const DETAIL_FIELDS = [
    ['businessName', 'Business / service name'], ['ownerName', 'Owner name'], ['category', 'Category'],
    ['mobile', 'Mobile'], ['email', 'Email'], ['capacity', 'Capacity'],
    ['price', 'Price text (e.g. From ₹15,000)'], ['priceMin', 'Lowest price (₹)'], ['priceMax', 'Highest price (₹)'],
];

const PhotoStrip = ({ photos, onRemove, onAdd, busy, max }) => {
    const input = useRef(null);
    return (
        <div className="psm-photos">
            {(photos || []).map(photo => (
                <div className="psm-photo" key={photo}>
                    <img src={fileUrl(photo)} alt="" loading="lazy" />
                    <button type="button" onClick={() => onRemove(photo)} aria-label="Remove photo"><X size={12} /></button>
                </div>
            ))}
            {(photos || []).length < max && (
                <button type="button" className="psm-photo-add" disabled={busy} onClick={() => input.current?.click()}>
                    <Plus size={16} /> {busy ? 'Uploading…' : 'Add photos'}
                </button>
            )}
            <input ref={input} type="file" accept="image/*" multiple hidden onChange={(e) => { const files = [...e.target.files]; e.target.value = ''; if (files.length) onAdd(files); }} />
        </div>
    );
};

// ── Details ──
const DetailsTab = ({ service, reload, onDeleted }) => {
    const [form, setForm] = useState({});
    const [saving, setSaving] = useState(false);
    const [photoBusy, setPhotoBusy] = useState(false);
    const [pinNote, setPinNote] = useState('');
    const [confirm, confirmDialog] = useConfirmDialog();
    const { countries, states, districts, cities } = useLocationLists(form.country, form.state, form.district);
    const isIndia = String(form.country || '').trim().toLowerCase() === 'india';

    useEffect(() => {
        const next = {};
        DETAIL_FIELDS.forEach(([key]) => { next[key] = service[key] ?? ''; });
        next.description = service.description || '';
        // older services kept the whole address in one box: it becomes address line 1
        next.addressLine1 = service.addressLine1 || service.address || '';
        next.addressLine2 = service.addressLine2 || '';
        next.country = service.country || 'India';
        next.state = service.state || '';
        next.district = service.district || '';
        next.city = service.city || '';
        next.pincode = service.pincode || '';
        next.isActive = service.isActive !== false;
        next.isFeatured = Boolean(service.isFeatured);
        setForm(next);
        setPinNote('');
    }, [service]);

    const set = (key, value) => setForm(prev => ({ ...prev, [key]: value }));

    // Typing a full 6-digit Indian PIN code fills country, state, district and city
    const changePin = (raw) => {
        const pin = isIndia ? raw.replace(/\D/g, '').slice(0, 6) : raw.slice(0, 12);
        set('pincode', pin);
        setPinNote('');
        if (!isIndia || pin.length !== 6) return;
        setPinNote('Finding the place…');
        lookupPin(pin).then(place => {
            if (!place) { setPinNote('PIN code not found. Choose the place yourself.'); return; }
            const district = place.districtOther || place.district;
            setForm(prev => (prev.pincode === pin ? { ...prev, country: place.country, state: place.state, district, city: place.city } : prev));
            setPinNote(`Filled from PIN code: ${[place.city, district, place.state].filter(Boolean).join(', ')}`);
        });
    };

    const save = async (event) => {
        event.preventDefault();
        setSaving(true);
        try {
            await API.put(`${BASE}/services/${service._id}`, form);
            toast.success('Service updated');
            await reload(true);
        } catch (error) { toast.error(errorText(error, 'Could not save the service')); } finally { setSaving(false); }
    };

    const addPhotos = async (files) => {
        setPhotoBusy(true);
        try {
            const data = new FormData();
            files.forEach(file => data.append('photos', file));
            await API.post(`${BASE}/services/${service._id}/photos`, data, { headers: { 'Content-Type': 'multipart/form-data' } });
            toast.success('Photos added');
            await reload(true);
        } catch (error) { toast.error(errorText(error, 'Could not upload the photos')); } finally { setPhotoBusy(false); }
    };

    const removePhoto = async (url) => {
        if (!(await confirm({ title: 'Remove this photo?', message: 'The photo is taken off this service.', confirmLabel: 'Remove', danger: true }))) return;
        try { await API.delete(`${BASE}/services/${service._id}/photos`, { params: { url } }); await reload(true); } catch (error) { toast.error(errorText(error, 'Could not remove the photo')); }
    };

    const remove = async () => {
        if (!(await confirm({
            title: `Delete "${service.businessName}"?`,
            message: 'The service is removed from the site.',
            items: ['It is hidden everywhere on the site', 'Past bookings keep their history', 'Its packages are hidden too'],
            confirmLabel: 'Delete service', danger: true,
        }))) return;
        try {
            await API.delete(`${BASE}/services/${service._id}`);
            toast.success('Service deleted');
            onDeleted();
        } catch (error) {
            if (error.response?.data?.code === 'UPCOMING_BOOKINGS') {
                if (!(await confirm({ title: 'This service has upcoming bookings', message: `${error.response.data.message} Delete the service anyway?`, confirmLabel: 'Delete anyway', danger: true }))) return;
                try { await API.delete(`${BASE}/services/${service._id}`, { params: { force: 1 } }); toast.success('Service deleted'); onDeleted(); } catch (e) { toast.error(errorText(e, 'Could not delete the service')); }
                return;
            }
            toast.error(errorText(error, 'Could not delete the service'));
        }
    };

    return (
        <form className="psm-form" onSubmit={save}>
            {confirmDialog}
            <div className="psm-grid">
                {DETAIL_FIELDS.map(([key, label]) => (
                    <label key={key}><span>{label}</span>
                        <input value={form[key] ?? ''} onChange={e => set(key, e.target.value)} inputMode={key.startsWith('price') && key !== 'price' ? 'numeric' : undefined} />
                    </label>
                ))}
            </div>

            <h4 className="psm-sub"><MapPin size={14} /> Business address</h4>
            <div className="psm-grid">
                <label className="psm-wide"><span>Address line 1</span><input value={form.addressLine1 || ''} onChange={e => set('addressLine1', e.target.value)} placeholder="Building, street" maxLength={200} /></label>
                <label className="psm-wide"><span>Address line 2</span><input value={form.addressLine2 || ''} onChange={e => set('addressLine2', e.target.value)} placeholder="Area, landmark (optional)" maxLength={200} /></label>
                <div className="psm-field"><span>Country</span>
                    <Select value={form.country} options={countries} allowEmpty={false} ariaLabel="Country"
                        onChange={v => setForm(prev => ({ ...prev, country: v, state: '', district: '', city: '' }))} />
                </div>
                <div className="psm-field"><span>State</span>
                    <Select value={form.state} options={states} ariaLabel="State" disabled={!form.country} placeholder={form.country ? 'Select state' : 'Select country first'}
                        onChange={v => setForm(prev => ({ ...prev, state: v, district: '', city: '' }))} />
                </div>
                <div className="psm-field"><span>District</span>
                    <Select value={form.district} options={[...new Set([form.district, ...districts].filter(Boolean))]} ariaLabel="District" disabled={!form.state} placeholder={form.state ? 'Select district' : 'Select state first'}
                        onChange={v => setForm(prev => ({ ...prev, district: v, city: '' }))} />
                </div>
                <div className="psm-field"><span>City</span>
                    <Select value={form.city} options={[...new Set([form.city, ...cities].filter(Boolean))]} ariaLabel="City" disabled={!form.state} placeholder={form.state ? 'Select city' : 'Select state first'}
                        onChange={v => set('city', v)} />
                </div>
                <label><span>{isIndia ? 'PIN code' : 'Postal code'}</span>
                    <input value={form.pincode || ''} onChange={e => changePin(e.target.value)} inputMode={isIndia ? 'numeric' : 'text'} maxLength={12} placeholder={isIndia ? '6-digit PIN code' : 'Postal code'} />
                    {pinNote && <small className="psm-note">{pinNote}</small>}
                </label>
            </div>

            <div className="psm-grid" style={{ marginTop: 14 }}>
                <label className="psm-wide"><span>Description</span><textarea rows={4} value={form.description || ''} onChange={e => set('description', e.target.value)} /></label>
            </div>
            <div className="psm-toggles">
                <label><input type="checkbox" checked={Boolean(form.isActive)} onChange={e => set('isActive', e.target.checked)} /> Active (visible on the site)</label>
                <label><input type="checkbox" checked={Boolean(form.isFeatured)} onChange={e => set('isFeatured', e.target.checked)} /> Featured</label>
            </div>
            <h4 className="psm-sub"><ImageIcon size={14} /> Service photos <small>{(service.photos || []).length}/15</small></h4>
            <PhotoStrip photos={service.photos} onRemove={removePhoto} onAdd={addPhotos} busy={photoBusy} max={15} />
            <div className="psm-actions">
                <button type="button" className="psm-danger" onClick={remove}><Trash2 size={15} /> Delete service</button>
                <button type="submit" className="psm-primary" disabled={saving}><Save size={15} /> {saving ? 'Saving…' : 'Save changes'}</button>
            </div>
        </form>
    );
};

// ── Packages ──
const blankPackage = { name: '', price: '', description: '', features: '' };
const MAX_PACKAGE_PHOTOS = 5;
const MAX_PHOTO_MB = 5;

// Themed photo box for a package: the photos it already has (removable), the new ones chosen but not saved yet (with a preview),
// and an "Add photos" tile that also accepts photos dropped on it.
const PackagePhotoPicker = ({ existing, onRemoveExisting, staged, onStage, onUnstage, max }) => {
    const input = useRef(null);
    const [over, setOver] = useState(false);
    const previews = useMemo(() => staged.map(file => URL.createObjectURL(file)), [staged]);
    useEffect(() => () => previews.forEach(url => URL.revokeObjectURL(url)), [previews]);
    const room = max - existing.length - staged.length;

    const take = (list) => {
        const images = [...list].filter(file => file.type.startsWith('image/'));
        if (images.length < list.length) toast.error('Only image files can be added');
        const small = images.filter(file => file.size <= MAX_PHOTO_MB * 1024 * 1024);
        if (small.length < images.length) toast.error(`Each photo can be at most ${MAX_PHOTO_MB} MB`);
        if (small.length > room) toast.error(room > 0 ? `Only ${room} more photo${room === 1 ? '' : 's'} can be added` : `A package can have at most ${max} photos`);
        if (small.length && room > 0) onStage(small.slice(0, room));
    };

    return (
        <div className="psm-wide psm-picker">
            <span className="psm-picker__label">Package photos <small>{existing.length + staged.length}/{max}</small></span>
            <div className={`psm-photos ${over ? 'is-over' : ''}`}
                onDragOver={(e) => { e.preventDefault(); setOver(true); }} onDragLeave={() => setOver(false)}
                onDrop={(e) => { e.preventDefault(); setOver(false); take(e.dataTransfer.files); }}>
                {existing.map(photo => (
                    <div className="psm-photo" key={photo}>
                        <img src={fileUrl(photo)} alt="" loading="lazy" />
                        <button type="button" onClick={() => onRemoveExisting(photo)} aria-label="Remove photo"><X size={12} /></button>
                    </div>
                ))}
                {staged.map((file, index) => (
                    <div className="psm-photo is-new" key={`${file.name}-${file.size}-${index}`} title={file.name}>
                        <img src={previews[index]} alt="" />
                        <span className="psm-photo__new">New</span>
                        <button type="button" onClick={() => onUnstage(index)} aria-label="Remove photo"><X size={12} /></button>
                    </div>
                ))}
                {room > 0 && (
                    <button type="button" className="psm-photo-add" onClick={() => input.current?.click()}>
                        <Plus size={16} /> Add photos
                    </button>
                )}
                <input ref={input} type="file" accept="image/*" multiple hidden onChange={(e) => { const list = [...e.target.files]; e.target.value = ''; if (list.length) take(list); }} />
            </div>
            <small className="psm-note">JPG, PNG or WebP · up to {MAX_PHOTO_MB} MB each · you can also drop photos here</small>
        </div>
    );
};

const PackageForm = ({ serviceId, menu, onDone, onCancel }) => {
    const [form, setForm] = useState(menu
        ? { name: menu.name || '', price: menu.price || '', description: menu.description || '', features: (menu.features || []).join('\n') }
        : blankPackage);
    const [files, setFiles] = useState([]);
    const [existing, setExisting] = useState(menu?.photos || []);
    const [saving, setSaving] = useState(false);
    const [confirm, confirmDialog] = useConfirmDialog();

    const removeExisting = async (photo) => {
        if (!(await confirm({ title: 'Remove this photo?', message: 'The photo is taken off this package.', confirmLabel: 'Remove', danger: true }))) return;
        try {
            await API.delete(`${BASE}/menus/${menu._id}/photos`, { params: { url: photo } });
            setExisting(list => list.filter(item => item !== photo));
        } catch (error) { toast.error(errorText(error, 'Could not remove the photo')); }
    };

    const submit = async (event) => {
        event.preventDefault();
        if (!form.name.trim()) { toast.error('Package name is required'); return; }
        setSaving(true);
        try {
            const data = new FormData();
            Object.entries(form).forEach(([key, value]) => data.append(key, value));
            files.forEach(file => data.append('photos', file));
            const config = { headers: { 'Content-Type': 'multipart/form-data' } };
            if (menu) await API.put(`${BASE}/menus/${menu._id}`, data, config); else await API.post(`${BASE}/services/${serviceId}/menus`, data, config);
            toast.success(menu ? 'Package updated' : 'Package added');
            onDone();
        } catch (error) { toast.error(errorText(error, 'Could not save the package')); } finally { setSaving(false); }
    };

    return (
        <form className="psm-package-form" onSubmit={submit}>
            {confirmDialog}
            <div className="psm-grid">
                <label><span>Package name *</span><input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} /></label>
                <label><span>Price</span><input value={form.price} onChange={e => setForm({ ...form, price: e.target.value })} placeholder="e.g. ₹25,000" /></label>
                <label className="psm-wide"><span>Description</span><textarea rows={3} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} /></label>
                <label className="psm-wide"><span>What is included (one per line)</span><textarea rows={4} value={form.features} onChange={e => setForm({ ...form, features: e.target.value })} /></label>
                <PackagePhotoPicker existing={existing} onRemoveExisting={removeExisting} staged={files}
                    onStage={(added) => setFiles(list => [...list, ...added])} onUnstage={(index) => setFiles(list => list.filter((_, i) => i !== index))} max={MAX_PACKAGE_PHOTOS} />
            </div>
            <div className="psm-actions">
                <button type="button" onClick={onCancel}>Cancel</button>
                <button type="submit" className="psm-primary" disabled={saving}><Save size={15} /> {saving ? 'Saving…' : menu ? 'Save package' : 'Add package'}</button>
            </div>
        </form>
    );
};

const PackagesTab = ({ serviceId, menus, reload }) => {
    const [editing, setEditing] = useState(null); // a menu, 'new' or null
    const [confirm, confirmDialog] = useConfirmDialog();

    const act = async (request, okText) => {
        try { await request(); toast.success(okText); await reload(true); } catch (error) { toast.error(errorText(error, 'Action failed')); }
    };

    const removePackagePhoto = async (menu, photo) => {
        if (!(await confirm({ title: 'Remove this photo?', message: `The photo is taken off the package "${menu.name}".`, confirmLabel: 'Remove', danger: true }))) return;
        act(() => API.delete(`${BASE}/menus/${menu._id}/photos`, { params: { url: photo } }), 'Photo removed');
    };

    const removePackage = async (menu) => {
        if (!(await confirm({ title: `Delete the package "${menu.name}"?`, message: 'The package and its photos are removed. This cannot be undone.', confirmLabel: 'Delete package', danger: true }))) return;
        act(() => API.delete(`${BASE}/menus/${menu._id}`), 'Package deleted');
    };

    if (editing) {
        return <PackageForm serviceId={serviceId} menu={editing === 'new' ? null : editing} onCancel={() => setEditing(null)} onDone={async () => { setEditing(null); await reload(true); }} />;
    }

    return (
        <div>
            {confirmDialog}
            <div className="psm-bar"><span>{menus.length} package{menus.length === 1 ? '' : 's'}</span><button type="button" className="psm-primary" onClick={() => setEditing('new')}><Plus size={15} /> Add package</button></div>
            {menus.length === 0 ? <p className="psm-muted">This service has no packages yet.</p> : (
                <div className="psm-packages">
                    {menus.map(menu => (
                        <article className={`psm-package ${menu.isActive ? '' : 'is-off'}`} key={menu._id}>
                            <div className="psm-package__head">
                                <div><h4>{menu.name}</h4>{menu.price && <strong>{menu.price}</strong>}</div>
                                <span className={`psm-badge ${menu.isActive ? 'is-on' : ''}`}>{menu.isActive ? 'Active' : 'Hidden'}</span>
                            </div>
                            {menu.description && <p>{menu.description}</p>}
                            {(menu.features || []).length > 0 && <ul>{menu.features.map((f, i) => <li key={`${f}-${i}`}>{f}</li>)}</ul>}
                            {(menu.photos || []).length > 0 && (
                                <div className="psm-photos">
                                    {menu.photos.map(photo => (
                                        <div className="psm-photo" key={photo}><img src={fileUrl(photo)} alt="" loading="lazy" />
                                            <button type="button" aria-label="Remove photo" onClick={() => removePackagePhoto(menu, photo)}><X size={12} /></button>
                                        </div>
                                    ))}
                                </div>
                            )}
                            <div className="psm-package__actions">
                                <button type="button" onClick={() => setEditing(menu)}><Pencil size={14} /> Edit</button>
                                <button type="button" onClick={() => act(() => API.put(`${BASE}/menus/${menu._id}/toggle`), menu.isActive ? 'Package hidden' : 'Package active')}>{menu.isActive ? 'Hide' : 'Show'}</button>
                                <button type="button" className="psm-danger" onClick={() => removePackage(menu)}><Trash2 size={14} /> Delete</button>
                            </div>
                        </article>
                    ))}
                </div>
            )}
        </div>
    );
};

// ── Calendar ──
const WEEK = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const STATUS_TEXT = { available: 'Available', blocked: 'Blocked', booked: 'Booked', pending: 'Booking request' };

const CalendarTab = ({ service, setAvailability }) => {
    const [month, setMonth] = useState(() => { const now = new Date(); return new Date(now.getFullYear(), now.getMonth(), 1); });
    const [range, setRange] = useState({ from: '', to: '', status: 'blocked' });
    const [busy, setBusy] = useState(false);

    const byDay = useMemo(() => {
        const map = new Map();
        (service.availability || []).forEach(entry => map.set(utcKey(entry.date), entry));
        return map;
    }, [service.availability]);

    const cells = useMemo(() => {
        const first = new Date(month.getFullYear(), month.getMonth(), 1);
        const days = new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate();
        return [...Array(first.getDay()).fill(null), ...Array.from({ length: days }, (_, i) => new Date(month.getFullYear(), month.getMonth(), i + 1))];
    }, [month]);

    const monthCount = (status) => cells.filter(d => d && (byDay.get(keyOf(d))?.status === status)).length;

    const run = async (request) => {
        setBusy(true);
        try { const res = await request(); setAvailability(res.data.availability || []); toast.success(res.data.message || 'Calendar updated'); } catch (error) { toast.error(errorText(error, 'Could not update the calendar')); } finally { setBusy(false); }
    };

    const clickDay = (date) => {
        const entry = byDay.get(keyOf(date));
        const status = entry?.status || 'available';
        if (status === 'booked' || status === 'pending') { toast(`This date has a ${status === 'booked' ? 'confirmed booking' : 'booking request'}. Manage it from Bookings.`); return; }
        run(() => API.put(`${BASE}/services/${service._id}/availability/day`, { date: keyOf(date), status: status === 'blocked' ? 'available' : 'blocked' }));
    };

    const applyRange = () => {
        if (!range.from || !range.to) { toast.error('Choose a from and a to date'); return; }
        run(() => API.put(`${BASE}/services/${service._id}/availability/range`, range));
    };

    return (
        <div>
            <div className="psm-month">
                <button type="button" onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() - 1, 1))} aria-label="Previous month"><ChevronLeft size={16} /></button>
                <strong>{month.toLocaleDateString('en-IN', { month: 'long', year: 'numeric' })}</strong>
                <button type="button" onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() + 1, 1))} aria-label="Next month"><ChevronRight size={16} /></button>
            </div>
            <div className="psm-cal">
                {WEEK.map(day => <span className="psm-cal__head" key={day}>{day}</span>)}
                {cells.map((date, index) => {
                    if (!date) return <span key={`e${index}`} />;
                    const status = byDay.get(keyOf(date))?.status || 'available';
                    return (
                        <button type="button" key={keyOf(date)} disabled={busy} className={`psm-day is-${status}`} title={STATUS_TEXT[status]} onClick={() => clickDay(date)}>{date.getDate()}</button>
                    );
                })}
            </div>
            <div className="psm-legend">
                {['available', 'blocked', 'pending', 'booked'].map(status => <span key={status}><i className={`is-${status}`} />{STATUS_TEXT[status]}{status !== 'available' ? ` (${monthCount(status)})` : ''}</span>)}
            </div>
            <p className="psm-muted">Click a date to block or unblock it. Dates held by a booking can only be changed from that booking.</p>

            <h4 className="psm-sub"><CalendarDays size={14} /> Change a range of dates</h4>
            <div className="psm-range">
                <div><span>From</span><DatePicker value={range.from} onChange={v => setRange(prev => ({ ...prev, from: v, to: prev.to && prev.to < v ? v : prev.to }))} placeholder="Select date" /></div>
                <div><span>To</span><DatePicker value={range.to} min={range.from} onChange={v => setRange(prev => ({ ...prev, to: v }))} placeholder="Select date" /></div>
                <div><span>Mark as</span>
                    <Select value={range.status} onChange={v => setRange(prev => ({ ...prev, status: v }))} allowEmpty={false} ariaLabel="Mark dates as"
                        options={[{ value: 'blocked', label: 'Blocked' }, { value: 'available', label: 'Available' }]} />
                </div>
                <button type="button" className="psm-primary" disabled={busy} onClick={applyRange}>Apply</button>
            </div>
        </div>
    );
};

// ── the window ──
const ProviderServiceManager = ({ serviceId, onClose, onChanged }) => {
    const [data, setData] = useState(null); // { service, menus, bookingCount }
    const [tab, setTab] = useState('details');
    const [failed, setFailed] = useState(false);

    const load = useCallback(async (changed = false) => {
        try {
            const res = await API.get(`${BASE}/services/${serviceId}`);
            setData(res.data);
            if (changed) onChanged?.();
        } catch { setFailed(true); }
    }, [serviceId, onChanged]);

    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        // Esc closes an open confirmation pop-up first, not the whole window behind it
        const onKey = (e) => { if (e.key === 'Escape' && !document.querySelector('.confirm-dialog-overlay')) onClose(); };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [onClose]);

    const setAvailability = (availability) => setData(prev => ({ ...prev, service: { ...prev.service, availability } }));

    const tabs = [['details', 'Details', FileText], ['packages', `Packages${data ? ` (${data.menus.length})` : ''}`, Package], ['calendar', 'Calendar', CalendarDays]];

    return (
        <div className="psm-overlay" role="presentation" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
            <section className="psm-window" role="dialog" aria-modal="true" aria-label="Manage service">
                <header>
                    <div><small>Manage service</small><h3>{data?.service?.businessName || 'Loading…'}</h3></div>
                    <button type="button" onClick={onClose} aria-label="Close"><X size={18} /></button>
                </header>
                <nav>
                    {tabs.map(([id, label, Icon]) => <button type="button" key={id} className={tab === id ? 'is-active' : ''} onClick={() => setTab(id)}><Icon size={15} /> {label}</button>)}
                </nav>
                <div className="psm-body">
                    {failed ? <p className="psm-muted">Could not load this service.</p> : !data ? <p className="psm-muted">Loading…</p> : (
                        <>
                            {tab === 'details' && <DetailsTab service={data.service} reload={load} onDeleted={() => { onChanged?.(); onClose(); }} />}
                            {tab === 'packages' && <PackagesTab serviceId={data.service._id} menus={data.menus} reload={load} />}
                            {tab === 'calendar' && <CalendarTab service={data.service} setAvailability={setAvailability} />}
                        </>
                    )}
                </div>
            </section>
        </div>
    );
};

export default ProviderServiceManager;
